import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../context/LanguageContext';
import PropertyCard from '../components/PropertyCard';
import './PartnerPage.css';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const COORDS = {
  'Limassol':   [34.6841, 33.0392], 'Nicosia':   [35.1856, 33.3823],
  'Paphos':     [34.7754, 32.4219], 'Larnaca':   [34.9003, 33.6233],
  'Famagusta':  [35.1213, 33.9408], 'Ayia Napa': [34.9843, 34.0000],
  'Pafos':      [34.7754, 32.4219], 'Germasogeia': [34.6823, 33.0823],
};

function mapRow(row) {
  return {
    id:               row.id,
    title:            row.title,
    price:            row.price,
    priceType:        row.price_label,
    type:             row.type,
    bedrooms:         row.beds,
    bathrooms:        row.baths,
    size:             row.sqm,
    city:             row.city,
    region:           row.area,
    description:      row.description,
    features:         row.features || [],
    is_boosted:       row.is_boosted || false,
    boost_expires_at: row.boost_expires_at || null,
    is_partner:       true,
    image:            row.images?.[0] || null,
    images:           row.images || [],
    gradient:         '#F2F1ED',
    agent: {
      name:   row.contact_name  || 'Partner Agent',
      phone:  row.contact_phone || '+357 25 000 000',
      agency: row.agency_name   || 'Partner Agency',
    },
  };
}

function PartnerMap({ listings }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!mapRef.current) return;
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, { zoomControl: false, scrollWheelZoom: false });
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '© OpenStreetMap © CARTO', subdomains: 'abcd', maxZoom: 19,
      }).addTo(mapInstanceRef.current);
      L.control.zoom({ position: 'bottomright' }).addTo(mapInstanceRef.current);
      mapInstanceRef.current.setView([34.9, 33.1], 8);
    }
    const map = mapInstanceRef.current;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    listings.forEach((listing, i) => {
      const coords = COORDS[listing.region] || COORDS[listing.city] ||
        COORDS[Object.keys(COORDS).find(k => k.toLowerCase() === (listing.city || '').toLowerCase())];
      if (!coords) return;
      const lat = coords[0] + (i % 3 - 1) * 0.005;
      const lng = coords[1] + (i % 2 === 0 ? 0.006 : -0.006);
      const price = listing.priceType === 'per month'
        ? `€${listing.price?.toLocaleString()}/mo`
        : `€${listing.price?.toLocaleString()}`;
      const icon = L.divIcon({
        className: '',
        html: `<div class="lp-pin-wrap"><div class="lp-pin-circle">${price}</div></div>`,
        iconSize: [80, 32], iconAnchor: [40, 16],
      });
      const marker = L.marker([lat, lng], { icon }).addTo(map);
      marker.on('click', () => navigate(`/property/${listing.id}`));
      markersRef.current.push(marker);
    });
  }, [listings, navigate]);

  return <div ref={mapRef} style={{ width: '100%', height: '100%' }} />;
}

export default function PartnerPage() {
  const { t } = useLanguage();
  const { slugOrId } = useParams();
  const navigate = useNavigate();

  const [agency, setAgency]   = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const [priceType, setPriceType]     = useState('all');
  const [propertyType, setPropertyType] = useState('all');
  const [sortBy, setSortBy]           = useState('newest');

  useEffect(() => {
    if (!slugOrId) return;
    setLoading(true);
    setNotFound(false);
    setLoadError(false);

    (async () => {
      try {
        // Try slug first, then fall back to id
        const { data: agencyData, error } = await supabase
          .from('agencies')
          .select('*')
          .or(`slug.eq.${slugOrId},id.eq.${slugOrId}`)
          .maybeSingle();

        if (error) {
          setLoadError(true);
          setLoading(false);
          return;
        }
        if (!agencyData) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setAgency(agencyData);

        const { data: listingData, error: listingsErr } = await supabase
          .from('listings')
          .select('*')
          .eq('agency_id', agencyData.id)
          .not('status', 'in', '(archived,deleted)')
          .order('created_at', { ascending: false });

        if (listingsErr) {
          setLoadError(true);
          setLoading(false);
          return;
        }

        setListings((listingData || []).map(mapRow));
        setLoading(false);
      } catch (err) {
        console.error('Partner page load failed:', err);
        setLoadError(true);
        setLoading(false);
      }
    })();
  }, [slugOrId]);

  // 10s watchdog — if still loading after 10 seconds, surface an error state.
  useEffect(() => {
    setLoadTimedOut(false);
    if (!loading) return;
    const timer = setTimeout(() => setLoadTimedOut(true), 10000);
    return () => clearTimeout(timer);
  }, [loading, slugOrId]);

  if (notFound) return (
    <div style={{ padding: '80px 48px', textAlign: 'center' }}>
      <h2>{t('partner.notFound')}</h2>
      <button
        onClick={() => navigate('/')}
        style={{ marginTop: 16, padding: '10px 24px', background: '#0E0E10', color: 'white', border: 'none', borderRadius: 0, cursor: 'pointer', fontWeight: 700 }}
      >
        {t('partner.backHome')}
      </button>
    </div>
  );

  if (loadError || (loading && loadTimedOut)) return (
    <div className="partner-load-error">
      <h2>This is taking longer than expected.</h2>
      <p>Something on our end is slow to respond. Please try again.</p>
      <div className="partner-load-error__actions">
        <button className="partner-load-error__btn partner-load-error__btn--primary" onClick={() => window.location.reload()}>
          Try again
        </button>
        <button className="partner-load-error__btn partner-load-error__btn--outline" onClick={() => navigate(-1)}>
          Go back
        </button>
      </div>
    </div>
  );

  if (loading || !agency) return (
    <div style={{ padding: '120px 48px', textAlign: 'center', color: '#9CA3AF' }}>
      {t('partner.loading')}
    </div>
  );

  const displayListings = listings
    .filter(l => {
      if (priceType === 'rent') return l.priceType === 'per month';
      if (priceType === 'sale') return l.priceType !== 'per month';
      return true;
    })
    .filter(l => propertyType === 'all' || l.type?.toLowerCase() === propertyType)
    .sort((a, b) => {
      if (sortBy === 'price-asc')  return a.price - b.price;
      if (sortBy === 'price-desc') return b.price - a.price;
      return 0;
    });

  return (
    <div className="partner-page">
      <div className="partner-page__hero">
        <div className="partner-page__hero-overlay" />
        <div className="partner-page__hero-content">
          <button className="mc-page__back" onClick={() => navigate(-1)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            {t('partner.back')}
          </button>
          <div className="partner-page__avatar">
            {agency.logo_url ? (
              <img src={agency.logo_url} alt={agency.name} style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 12 }} />
            ) : (
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            )}
          </div>
          <h1 className="mc-page__title">{agency.name}</h1>
          <p className="mc-page__sub">{agency.description}</p>
        </div>
      </div>

      <div className="partner-page__info">
        <div className="partner-page__info-inner">
          {[
            agency.cities_served?.length && {
              label: t('partner.citiesServed'),
              value: agency.cities_served.join(', '),
            },
            agency.phone && { label: t('partner.contactUs'), value: agency.phone },
            { label: t('partner.listings'), value: loading ? '—' : listings.length },
          ].filter(Boolean).map(item => (
            <div key={item.label} className="partner-page__info-item">
              <div className="partner-page__info-val">{item.value}</div>
              <div className="partner-page__info-label">{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="partner-page__filters">
        <div className="partner-filter-group">
          <label>{t('listings.listingType')}</label>
          <div className="partner-filter-chips">
            {['all', 'sale', 'rent'].map(type => (
              <button key={type} className={`partner-filter-chip${priceType === type ? ' partner-filter-chip--active' : ''}`} onClick={() => setPriceType(type)}>
                {type === 'all' ? t('listings.filters.all') : type === 'sale' ? t('listings.filters.forSale') : t('listings.filters.forRent')}
              </button>
            ))}
          </div>
        </div>
        <div className="partner-filter-group">
          <label>{t('listings.propertyType')}</label>
          <div className="partner-filter-chips">
            {['all', 'house', 'apartment', 'villa'].map(type => (
              <button key={type} className={`partner-filter-chip${propertyType === type ? ' partner-filter-chip--active' : ''}`} onClick={() => setPropertyType(type)}>
                {type === 'all' ? t('listings.filters.all') : t(`listings.filters.${type}`)}
              </button>
            ))}
          </div>
        </div>
        <div className="partner-filter-group partner-filter-group--right">
          <label>{t('listings.sortBy')}</label>
          <select className="partner-filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="newest">{t('listings.filters.newestFirst')}</option>
            <option value="price-asc">{t('listings.filters.priceLow')}</option>
            <option value="price-desc">{t('listings.filters.priceHigh')}</option>
          </select>
        </div>
      </div>

      <div className="partner-page__body">
        <div className="partner-page__map-col">
          <div className="partner-page__map-sticky">
            <PartnerMap listings={displayListings} />
          </div>
          {/* Ad slot hidden for now — restore by removing `style={{ display: 'none' }}`. */}
          <div className="partner-ad-slot" style={{ display: 'none' }}>
            <div className="partner-ad-slot__label">{t('listings.advertisement')}</div>
            <div className="partner-ad-slot__title">{t('listings.placeAd')}</div>
            <div className="partner-ad-slot__sub">{t('listings.adSub')}</div>
            <a href="mailto:cosmoestatescyprus@gmail.com" style={{ display:'inline-block', background:'#0E0E10', color:'white', fontSize:13, fontWeight:700, padding:'10px 20px', borderRadius:4, textDecoration:'none' }}>
              {t('listings.advertise')}
            </a>
          </div>
        </div>
        <div className="partner-page__listings-col">
          {displayListings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#9CA3AF' }}>{t('listings.noProperties')}</div>
          ) : (
            <div className="partner-page__grid">
              {displayListings.map((l, i) => <PropertyCard key={l.id} listing={l} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
